/**
 * Shop catalog infinite scroll — append the next page of products.
 */

import { syncFavouriteButtons } from './favourites.js';

/** Pixels below the fold that still count as the bottom preload zone. */
const PRELOAD_PX = 400;

/** @type {IntersectionObserver|null} */
let observer = null;

/** @type {AbortController|null} */
let loadRequest = null;

/** @type {boolean} */
let loading = false;

/** This visit to the preload zone already loaded its batch (and optional follow-up). */
let satisfied = false;

/** The one automatic extra page used when a batch does not fill the screen. */
let followUpUsed = false;

/** Next page to load without hiding the spinner, set just before the current request finishes. */
let queuedFollow = '';

/** @type {boolean} */
let scrollScheduled = false;

/**
 * Stop an in-flight page load (e.g. when filters replace the grid).
 */
export function abortShopInfinite() {
	queuedFollow = '';
	followUpUsed = false;
	satisfied = false;
	loadRequest?.abort();
	loadRequest = null;
	loading = false;
}

/**
 * @returns {HTMLElement|null}
 */
function root() {
	const el = document.querySelector('[data-dc-infinite]');
	return el instanceof HTMLElement ? el : null;
}

/**
 * Sentinel is near the bottom edge, not already scrolled past.
 *
 * @param {HTMLElement} sentinel
 */
function inBottomZone(sentinel) {
	const rect = sentinel.getBoundingClientRect();
	const viewHeight = window.innerHeight;
	if (rect.bottom <= 0 || rect.top >= viewHeight + PRELOAD_PX) {
		return false;
	}
	// Sitting in the top half means the customer has moved past the grid.
	if (rect.top < viewHeight * 0.5) {
		return false;
	}
	return true;
}

/**
 * @param {HTMLElement} el
 * @param {boolean} busy
 */
function setLoading(el, busy) {
	const status = el.querySelector('[data-dc-infinite-loading]');
	if (status instanceof HTMLElement) {
		status.hidden = !busy;
	}
	el.classList.toggle('is-loading', busy);
}

/**
 * @param {HTMLElement} el
 */
function showEnd(el) {
	const end = el.querySelector('[data-dc-infinite-end]');
	const sentinel = el.querySelector('[data-dc-infinite-sentinel]');
	if (end instanceof HTMLElement) {
		end.hidden = false;
	}
	if (sentinel instanceof HTMLElement) {
		sentinel.hidden = true;
	}
	el.dataset.next = '';
	if (sentinel instanceof Element) {
		observer?.unobserve(sentinel);
	}
}

/**
 * @param {string} href
 */
async function loadNext(href) {
	const el = root();
	if (!el || loading || !href) {
		return;
	}

	const list = document.querySelector('.dc-shop-main ul.products');
	if (!(list instanceof HTMLElement)) {
		return;
	}

	loading = true;
	loadRequest?.abort();
	const controller = new AbortController();
	loadRequest = controller;
	setLoading(el, true);

	try {
		const url = new URL(href, window.location.origin);
		url.searchParams.delete('dc_count');

		const response = await fetch(url, {
			signal: controller.signal,
			credentials: 'same-origin',
			cache: 'no-store',
			headers: { 'X-Requested-With': 'fetch' },
		});

		if (!response.ok) {
			throw new Error('Shop page request failed');
		}

		const html = await response.text();
		const doc = new DOMParser().parseFromString(html, 'text/html');
		// Theme cards render as articles (not WooCommerce <li.product> wrappers).
		const items = doc.querySelectorAll(
			'.dc-shop-main ul.products > .dc-product-card, .dc-shop-main ul.products > li'
		);

		if (!items.length) {
			showEnd(el);
			return;
		}

		const fragment = document.createDocumentFragment();
		items.forEach((item) => {
			fragment.append(document.importNode(item, true));
		});
		list.append(fragment);
		syncFavouriteButtons();

		const nextRoot = doc.querySelector('[data-dc-infinite]');
		const nextHref =
			nextRoot instanceof HTMLElement ? (nextRoot.dataset.next || '').trim() : '';
		const nextPage = nextRoot instanceof HTMLElement ? nextRoot.dataset.page || '' : '';
		const maxPage = nextRoot instanceof HTMLElement ? nextRoot.dataset.max || '' : '';

		el.dataset.next = nextHref;
		if (nextPage) {
			el.dataset.page = nextPage;
		}
		if (maxPage) {
			el.dataset.max = maxPage;
		}

		if (!nextHref) {
			showEnd(el);
			return;
		}

		const sentinel = el.querySelector('[data-dc-infinite-sentinel]');
		if (!followUpUsed && sentinel instanceof HTMLElement && inBottomZone(sentinel)) {
			followUpUsed = true;
			queuedFollow = nextHref;
			return;
		}

		// Stay paused only while the sentinel is still in the zone. Once the new
		// cards push it down, the next scroll can load another page.
		satisfied = sentinel instanceof HTMLElement && inBottomZone(sentinel);
		followUpUsed = false;
	} catch (error) {
		if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
			return;
		}
		// Leave the sentinel active so a later scroll can retry.
		satisfied = true;
	} finally {
		if (loadRequest === controller) {
			loadRequest = null;
			loading = false;
		}

		const follow = queuedFollow;
		queuedFollow = '';
		if (follow) {
			await loadNext(follow);
			return;
		}

		if (!loading) {
			const current = root();
			if (current) {
				setLoading(current, false);
			}
		}
	}
}

/**
 * Load the next page only while the sentinel sits in the bottom preload zone.
 * A sentinel already above the viewport does not qualify.
 */
function requestIfInZone() {
	const current = root();
	const target = current?.querySelector('[data-dc-infinite-sentinel]');
	if (!(target instanceof HTMLElement) || target.hidden) {
		return;
	}

	const rect = target.getBoundingClientRect();
	const outside = rect.bottom <= 0 || rect.top >= window.innerHeight + PRELOAD_PX;
	if (outside) {
		if (!loading) {
			satisfied = false;
			followUpUsed = false;
		}
		return;
	}

	if (!inBottomZone(target) || loading || satisfied) {
		return;
	}

	const href = (current?.dataset.next || '').trim();
	if (href) {
		loadNext(href);
	}
}

function onScroll() {
	if (scrollScheduled) {
		return;
	}
	scrollScheduled = true;
	requestAnimationFrame(() => {
		scrollScheduled = false;
		requestIfInZone();
	});
}

/**
 * Watch the sentinel under the product grid (call after filter refreshes too).
 */
export function bindShopInfinite() {
	abortShopInfinite();
	observer?.disconnect();
	observer = null;
	window.removeEventListener('scroll', onScroll);

	const el = root();
	if (!el) {
		return;
	}

	const nextHref = (el.dataset.next || '').trim();
	const sentinel = el.querySelector('[data-dc-infinite-sentinel]');
	const end = el.querySelector('[data-dc-infinite-end]');

	if (!nextHref) {
		if (end instanceof HTMLElement) {
			end.hidden = false;
		}
		if (sentinel instanceof HTMLElement) {
			sentinel.hidden = true;
		}
		return;
	}

	if (end instanceof HTMLElement) {
		end.hidden = true;
	}
	if (!(sentinel instanceof HTMLElement)) {
		return;
	}
	sentinel.hidden = false;

	observer = new IntersectionObserver(
		() => {
			requestIfInZone();
		},
		{
			root: null,
			rootMargin: '0px 0px 400px 0px',
			threshold: 0,
		}
	);

	observer.observe(sentinel);
	window.addEventListener('scroll', onScroll, { passive: true });
	requestIfInZone();
}
